import React, { useState, useEffect, useCallback } from 'react';
import { AlertCircle, CheckCircle, TrendingUp, Calendar, RefreshCw } from 'lucide-react';
import PageIdentifier from '../shared/PageIdentifier';
import SCREEN_IDS from '../../utils/screenIds';
import { useAuth } from '../../contexts/AuthContext';
import TwilioWhatsAppService from '../../services/TwilioWhatsAppService';

const SchemeDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [payments, setPayments] = useState([]);
  const [paymentStats, setPaymentStats] = useState(null);
  const [sendingReminder, setSendingReminder] = useState(false);

  const handleSendReminder = async () => {
    if (!selectedEnrollment) return;
    setSendingReminder(true);
    try {
      await TwilioWhatsAppService.sendSchemeReminder(selectedEnrollment.id);
      alert('WhatsApp reminder sent successfully!');
    } catch (err) {
      alert('Failed to send reminder: ' + err.message);
    } finally {
      setSendingReminder(false);
    }
  };

  const fetchEnrollments = useCallback(async (userId) => {
    try {
      setLoading(true);
      console.log('Fetching enrollments managed by user:', userId);
      // Changed: Now fetching enrollments created/managed by this user
      const response = await fetch(`/api/schemes/enrollments/active`);
      const result = await response.json();

      console.log('Enrollments response:', result);

      if (result.success) {
        // Filter enrollments created by current user
        const userEnrollments = result.data.filter(e => e.created_by === userId) || [];
        setEnrollments(userEnrollments);
        if (userEnrollments.length > 0) {
          setSelectedEnrollment(userEnrollments[0]);
          await fetchPayments(userEnrollments[0].id);
        } else {
          setError(null);
        }
      } else {
        setError(result.error || 'Failed to load enrollments');
      }
    } catch (err) {
      console.error('Enrollment fetch error:', err);
      setError('Error loading enrollments: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Fetch enrollments when user is available
    if (user && user.id) {
      fetchEnrollments(user.id);
    } else {
      setLoading(false);
      setError('User information not available');
    }
  }, [user, fetchEnrollments]);

  const fetchPayments = async (enrollmentId) => {
    try {
      const response = await fetch(`/api/schemes/payments/enrollment/${enrollmentId}`);
      const result = await response.json();

      if (result.success) {
        const paymentList = result.data || [];
        setPayments(paymentList);

        // Calculate stats
        const total = paymentList.length;
        const paid = paymentList.filter(p => p.is_paid === true).length;
        const pending = paymentList.filter(p => p.is_paid === false).length;
        const totalPaid = paymentList
          .filter(p => p.is_paid === true)
          .reduce((sum, p) => sum + (parseFloat(p.paid_amount) || 0), 0);

        setPaymentStats({
          total,
          paid,
          pending,
          totalPaid,
          completion: total > 0 ? Math.round((paid / total) * 100) : 0
        });
      }
    } catch (err) {
      setError('Error loading payments: ' + err.message);
    }
  };

  const handleSelectEnrollment = (enrollment) => {
    setSelectedEnrollment(enrollment);
    fetchPayments(enrollment.id);
  };

  const refreshData = async () => {
    if (user && user.id) {
      await fetchEnrollments(user.id);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getEnrollmentStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="text-center py-12">
          <RefreshCw className="animate-spin mx-auto text-amber-500 mb-4" size={32} />
          <p className="text-gray-600">Loading your schemes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <PageIdentifier pageId={SCREEN_IDS.SCHEMES.DASHBOARD} pageName="My Schemes" />
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">My Gold Schemes</h1>
          <p className="text-gray-500 mt-1">Track your gold monthly installment schemes</p>
        </div>
        <button
          onClick={refreshData}
          className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg font-medium transition flex items-center gap-2"
        >
          <RefreshCw size={18} /> Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {enrollments.length === 0 ? (
        <div className="bg-white rounded-lg shadow-lg p-12 text-center border border-amber-100">
          <TrendingUp size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600 text-lg">You don't have any gold schemes yet</p>
          <p className="text-gray-500 mt-2">Contact your jeweler to enroll in a scheme</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Left Column: Enrollment List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg border border-amber-100">
              <div className="bg-gradient-to-r from-amber-500 to-amber-600 p-4 rounded-t-lg">
                <h2 className="text-white font-bold flex items-center gap-2">
                  <TrendingUp size={20} /> My Enrollments
                </h2>
              </div>

              <div className="p-4 space-y-2 max-h-96 overflow-y-auto">
                {enrollments.map(enrollment => (
                  <button
                    key={enrollment.id}
                    onClick={() => handleSelectEnrollment(enrollment)}
                    className={`w-full text-left p-3 rounded-lg border-2 transition ${selectedEnrollment?.id === enrollment.id
                        ? enrollment.currency === 'USD'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-amber-500 bg-amber-50'
                        : enrollment.currency === 'USD'
                          ? 'border-blue-200 bg-blue-50/40 hover:border-blue-400'
                          : 'border-amber-200 bg-amber-50/40 hover:border-amber-300'
                      }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <p className="font-semibold text-gray-800 text-sm">{enrollment.customer_name || 'Unknown Customer'}</p>
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${enrollment.currency === 'USD'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-amber-100 text-amber-700'
                        }`}>
                        {enrollment.currency === 'USD' ? '$ USD' : '₹ INR'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600">{enrollment.scheme_name || 'Unknown Scheme'}</p>
                    <p className={`text-xs px-2 py-1 rounded inline-block mt-1 ${getEnrollmentStatusColor(enrollment.status || 'active')}`}>
                      {enrollment.status ? enrollment.status.charAt(0).toUpperCase() + enrollment.status.slice(1) : 'Active'}
                    </p>
                    <div className="mt-2 text-xs text-gray-600 space-y-1">
                      <div className="flex justify-between">
                        <span>Monthly:</span>
                        <span className={`font-semibold ${enrollment.currency === 'USD' ? 'text-blue-700' : 'text-amber-700'}`}>
                          {enrollment.currency === 'USD' ? '$' : '₹'}{parseFloat(enrollment.monthly_amount || 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Maturity:</span>
                        <span className="font-semibold">{new Date(enrollment.maturity_date).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right Columns: Details */}
          <div className="lg:col-span-3 space-y-6">
            {selectedEnrollment && paymentStats && (
              <>
                {/* Enrollment Summary */}
                <div className={`bg-white rounded-lg shadow-lg overflow-hidden ${selectedEnrollment.currency === 'USD' ? 'border border-blue-200' : 'border border-amber-100'}`}>
                  <div className={`bg-gradient-to-r p-4 flex items-center justify-between ${selectedEnrollment.currency === 'USD' ? 'from-blue-500 to-blue-600' : 'from-amber-500 to-amber-600'}`}>
                    <h3 className="text-white font-bold text-lg">{selectedEnrollment.scheme_name}</h3>
                    <span className="bg-white/20 text-white text-sm font-bold px-3 py-1 rounded-full">
                      {selectedEnrollment.currency === 'USD' ? '$ USD' : '₹ INR'}
                    </span>
                  </div>

                  <div className="p-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-gray-600 text-sm">Monthly Amount</p>
                        <p className={`text-2xl font-bold ${selectedEnrollment.currency === 'USD' ? 'text-blue-600' : 'text-amber-600'}`}>
                          {selectedEnrollment.currency === 'USD' ? '$' : '₹'}{selectedEnrollment.monthly_amount}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600 text-sm">Duration</p>
                        <p className="text-2xl font-bold text-gray-800">{selectedEnrollment.duration_months}</p>
                        <p className="text-xs text-gray-500">months</p>
                      </div>
                      <div>
                        <p className="text-gray-600 text-sm">Total Amount</p>
                        <p className={`text-2xl font-bold ${selectedEnrollment.currency === 'USD' ? 'text-blue-600' : 'text-amber-600'}`}>
                          {selectedEnrollment.currency === 'USD' ? '$' : '₹'}{selectedEnrollment.total_amount}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600 text-sm">Maturity Date</p>
                        <p className="text-lg font-bold text-gray-800">
                          {new Date(selectedEnrollment.maturity_date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>

                    <div className="mt-4 pt-4 border-t border-gray-200 flex items-center gap-3 flex-wrap">
                      <p className={`text-sm px-3 py-1 rounded-full inline-block font-semibold ${getEnrollmentStatusColor(selectedEnrollment.status || 'active')}`}>
                        {selectedEnrollment.status === 'active' ? (
                          <>
                            <CheckCircle className="inline mr-1" size={14} />
                            Status: Active
                          </>
                        ) : (
                          `Status: ${selectedEnrollment.status || 'Active'}`
                        )}
                      </p>
                      <button
                        onClick={handleSendReminder}
                        disabled={sendingReminder}
                        className="text-sm px-3 py-1 rounded-full bg-green-100 text-green-800 font-semibold hover:bg-green-200 transition disabled:opacity-50"
                      >
                        {sendingReminder ? 'Sending…' : '💬 WhatsApp Reminder'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Payment Progress */}
                <div className="bg-white rounded-lg shadow-lg border border-amber-100 p-6">
                  <h3 className="font-bold text-lg text-gray-800 mb-4">Payment Progress</h3>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                      <p className="text-gray-600 text-sm">Paid</p>
                      <p className="text-2xl font-bold text-green-600">{paymentStats.paid}</p>
                      <p className="text-xs text-gray-500 mt-1">of {paymentStats.total}</p>
                    </div>
                    <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                      <p className="text-gray-600 text-sm">Pending</p>
                      <p className="text-2xl font-bold text-yellow-600">{paymentStats.pending}</p>
                      <p className="text-xs text-gray-500 mt-1">months</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                      <p className="text-gray-600 text-sm">Total Paid</p>
                      <p className="text-2xl font-bold text-blue-600">
                        {selectedEnrollment.currency === 'USD' ? '$' : '₹'}{parseFloat(paymentStats.totalPaid || 0).toFixed(2)}
                      </p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                      <p className="text-gray-600 text-sm">Progress</p>
                      <p className="text-2xl font-bold text-amber-600">{paymentStats.completion}%</p>
                      <p className="text-xs text-gray-500 mt-1">complete</p>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-gradient-to-r from-amber-500 to-amber-600 h-3 rounded-full transition-all duration-300"
                      style={{ width: `${paymentStats.completion}%` }}
                    />
                  </div>
                </div>

                {/* Payment Schedule */}
                <div className="bg-white rounded-lg shadow-lg border border-amber-100">
                  <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-4 rounded-t-lg">
                    <h3 className="text-white font-bold flex items-center gap-2">
                      <Calendar size={20} /> Payment Schedule
                    </h3>
                  </div>

                  <div className="p-6">
                    {payments.length === 0 ? (
                      <p className="text-gray-600">No payment records found</p>
                    ) : (
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {payments.map((payment, idx) => (
                          <div
                            key={idx}
                            className={`p-4 rounded-lg border-l-4 ${payment.is_paid
                                ? 'border-l-green-500 bg-green-50'
                                : 'border-l-yellow-500 bg-yellow-50'
                              }`}
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="font-semibold text-gray-800">
                                  Month {payment.payment_month}
                                </p>
                                <p className="text-sm text-gray-600">
                                  Due: {new Date(payment.due_date).toLocaleDateString()}
                                </p>
                                {payment.is_paid && (
                                  <p className="text-xs text-green-600 mt-1">
                                    ✓ Paid on {payment.payment_date ? new Date(payment.payment_date).toLocaleDateString() : 'N/A'}
                                  </p>
                                )}
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-gray-800">
                                  {selectedEnrollment.currency === 'USD' ? '$' : '₹'}{parseFloat(payment.payment_amount || 0).toFixed(2)}
                                </p>
                                <p className={`text-xs px-2 py-1 rounded mt-1 inline-block font-semibold ${payment.is_paid ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                  {payment.is_paid ? 'Paid' : 'Pending'}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Key Information */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>💡 Tip:</strong> Keep track of your payment dates to avoid delays.
                    {selectedEnrollment.status &&
                      ` Once you complete all ${paymentStats.total} payments, you'll be eligible for redemption.`
                    }
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SchemeDashboard;
